import { useState, useEffect, useRef } from 'react';
import { ResponseError, Catalogue } from '@ynap/api-utils';
import { useConfig, useAppState } from '@ynap/immutables-provider';
import { raygun } from '@ynap/clientside-monitoring';

const { getPromoCode } = Catalogue;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const configSelector = (config: Record<string, any>) => ({
    baseUrl: config.global?.services.commerce.baseUrl,
    brandId: config.global?.application.brandId,
    clientId: config.global?.services.commerce.clientId,
    autoPromoEnabled: config.head?.pdp.autoPromoEnabled ?? false,
});

// this list isn't definitive but represents the ones we're using
export const marketingKey = {
    PROMO_CODE: 'promoCode0',
    PRICE_ADJUSTMENT: 'PriceAdjustment0',
    FINAL_PRICE: 'FinalPriceTotal',
    CODE_DESCRIPTION: 'CalculationCodeDescription0',
} as const;

export const promotionType = {
    MANUAL: 'manual',
    AUTO: 'auto',
    NONE: 'none',
} as const;

export type UsePromotionPdpPriceParams = {
    id: string;
    language: string;
    uberToken: string;
    enabled: boolean;
};

// eslint-disable-next-line @typescript-eslint/ban-types
type BaseMarketingKeys = (typeof marketingKey)[keyof typeof marketingKey] | (string & {});
type ApiResponse = {
    MarketingSpotData: {
        baseMarketingSpotActivityData: {
            properties: {
                baseMarketingKey: BaseMarketingKeys;
                baseMarketingValue: string;
            }[];
        }[];
    }[];
};

export const usePromotionPdpPrice = ({ id, language, uberToken, enabled }: UsePromotionPdpPriceParams) => {
    const { baseUrl, brandId, clientId, autoPromoEnabled }: Record<string, string> = useConfig(configSelector);
    const { country } = useAppState() || {};

    const [, setRerender] = useState(0);
    const cache = useRef(new Map<string, Record<BaseMarketingKeys, string>>());

    useEffect(() => {
        if (!enabled || cache.current.get(id)) {
            return;
        }

        let isCancelled = false;
        const makeRequest = async () => {
            try {
                const response = await fetch(getPromoCode({ baseUrl, brandId, country, language, options: { partnumber: id }, clientId, uberToken }));

                if (!response.ok) {
                    raygun.helpers.agent('send', {
                        error: new Error('usePromotionPdpPrice'),
                        tags: [`error-usePromotionPdpPrice`, `partNumber: ${id}`, `statusCode: ${response.status}`],
                    });
                    throw new ResponseError(response);
                }

                const json = (await response.json()) as ApiResponse;
                const formattedApiResults = json.MarketingSpotData[0]?.baseMarketingSpotActivityData[0]?.properties.reduce(
                    (acc, curr) => {
                        acc[curr.baseMarketingKey] = curr.baseMarketingValue;
                        return acc;
                    },
                    {} as Record<BaseMarketingKeys, string>,
                );

                if (!isCancelled) {
                    cache.current.set(id, formattedApiResults);
                    // Cache is a ref, so force a rerender once we've stored the new value
                    setRerender((r) => (r += 1));
                }
            } catch (err) {
                raygun.helpers.agent('send', {
                    error: new Error('usePromotionPdpPrice'),
                    tags: [`error-usePromotionPdpPrice`, `partNumber: ${id}`],
                    customData: {
                        err,
                    },
                });
            }
        };
        makeRequest();

        return () => {
            isCancelled = true;
        };
    }, [id]);

    const marketingProperties = cache.current.get(id);
    // determine the type of promotion applied (if any) from the marketing properties
    const hasPromotionApplied = !!(marketingProperties && marketingProperties[marketingKey.PRICE_ADJUSTMENT]);
    const promotionApplied = hasPromotionApplied
        ? marketingProperties[marketingKey.PROMO_CODE]
            ? promotionType.MANUAL
            : autoPromoEnabled // auto promo is controlled via config
              ? promotionType.AUTO
              : promotionType.NONE
        : promotionType.NONE;

    return { marketingProperties: marketingProperties ?? {}, promotionApplied };
};
